import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'umi';
import { Card, Flex, message, Space, Spin } from 'antd';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import GoogleAddressSearch from './GooglePlaceAutocomplete';
import { fetchTimeZone, getAPIKey } from './util';
import { ProForm } from '@ant-design/pro-components';
import TimeZoneSelector from '@/components/TimeZoneSelector';

const GOOGLE_MAPS_API_KEY = getAPIKey();
const LIBRARIES = ['places'];

interface IPlace {
  address?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
}

interface MapWithAutocompleteProps {
  place?: IPlace;
  onChange?: (addressData: {
    address?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
  }) => void;
  hasTimeZones?: boolean;
  height?: number;
  zoom?: number;
}

const MapWithAutocomplete: React.FC<MapWithAutocompleteProps> = ({
                                                                   place = {},
                                                                   onChange,
                                                                   hasTimeZones = false,
                                                                   height = 300,
                                                                   zoom = 15,
                                                                 }) => {
  const intl = useIntl();
  const [addressString, setAddressString] = useState(place.address || '');
  
  const [mapCenter, setMapCenter] = useState({
    lat: place.latitude,
    lng: place.longitude,
  });
  const [markerPosition, setMarkerPosition] = useState(
    place.latitude && place.longitude
      ? { lat: place.latitude, lng: place.longitude }
      : null,
  );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // Synchronize map view with external place data to maintain UI consistency
  useEffect(() => {
    if (place.latitude && place.longitude) {
      const newPosition = {
        lat: parseFloat(place.latitude),
        lng: parseFloat(place.longitude),
      };
      setMapCenter(newPosition);
      setMarkerPosition(newPosition);
    }
  }, [place.latitude, place.longitude]);

  useEffect(() => {
    setAddressString(place.address || '');
  }, [place.address]);


  const handleAddressStringOnlyChange = useCallback((newAddress: string) => {
    if (newAddress === addressString) return;
    onChange({
      address: newAddress,
      latitude: mapCenter.lat,
      longitude: mapCenter.lng,
    });
  }, [onChange, addressString, mapCenter]);
  const handleAddressSelect = useCallback(async (addressData: any) => {
    try {
      const { place, lat, lng } = addressData;
      const newPosition = { lat, lng };

      setMapCenter(newPosition);
      setMarkerPosition(newPosition);
      setAddressString(place.formatted_address);

      let addressResult = {
        address: place.formatted_address,
        latitude: lat,
        longitude: lng,
      };

      if (hasTimeZones) {
        try {
          const timezone = await fetchTimeZone(lat, lng);
          addressResult.timezone = timezone || place?.timezone;
        } catch (error) {

        }

      }
      onChange?.(addressResult);
    } catch (error) {
      console.error('Error handling address selection:', error);
      message.error(
        intl.formatMessage({
          id: 'component.mapWithAutoComplete.addressError',
          defaultMessage: 'Address not located. Please try a different address.',
        }),
      );
    }
  }, [hasTimeZones, onChange, intl]);

  const performReverseGeocode = useCallback(async (lat: number, lng: number) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, async (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const formattedAddress = results[0].formatted_address;
        setAddressString(formattedAddress);
        let addressResult = {
          address: formattedAddress,
          latitude: lat,
          longitude: lng,
        };

        if (hasTimeZones) {
          try {
            const timezone = await fetchTimeZone(lat, lng);
            addressResult = { ...addressResult, timezone };
          } catch (error) {
            console.warn('Failed to fetch timezone:', error);
          }
        }

        onChange?.(addressResult);
      } else {
        // If reverse geocoding fails, still provide coordinates
        onChange?.({
          latitude: lat,
          longitude: lng,
          address: addressString,
        });
      }
    });
  }, [hasTimeZones, onChange]);

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const newPosition = { lat, lng };

      setMarkerPosition(newPosition);
      setMapCenter(newPosition);

      performReverseGeocode(lat, lng);
    }
  }, [performReverseGeocode]);

  if (loadError) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {intl.formatMessage({
            id: 'component.mapWithAutoComplete.loadError',
            defaultMessage: 'Error loading maps',
          })}
        </div>
      </Card>
    );
  }

  if (!isLoaded) {
    return <Spin />;
  }

  return (
    <Space direction="vertical" style={{ width: '100%', marginBottom: -30 }}>
      <Flex gap={10}>
        <ProForm.Item required rules={[{ required: true }]} style={{ width: '100%', marginBottom: 0 }}>
          <GoogleAddressSearch onBlur={handleAddressStringOnlyChange} value={addressString}
                               onChange={handleAddressSelect} />
        </ProForm.Item>
        <ProForm.Item
          style={{ marginBottom: 0 }}
          required
          rules={[{ required: true }]}
          name={'timezone'}
        >
          <TimeZoneSelector
            value={place.timezone}
            onChange={value => onChange?.({
              timezone: value,
            })}
            allowClear={false}
            variant="underlined"
            popupMatchSelectWidth={false}
            showSearch
            style={{
              width: 200,
            }}
            minWidth={100}
            placeholder={intl.formatMessage({
              id: 'pages.locations.selectTimeZonePlaceholder',
              defaultMessage: 'Select time zone',
            })}
          />
        </ProForm.Item>
      </Flex>
      {(mapCenter.lat && mapCenter.lng) ? (<div style={{ height: `${height}px`, width: '100%' }}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%', borderRadius: 6 }}
          center={mapCenter}
          zoom={zoom}
          onClick={handleMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            zoomControl: true,
            fullscreenControl: false,
          }}
        >
          {markerPosition && (
            <Marker
              position={markerPosition}
              draggable={true}
              onDragEnd={(event) => {
                if (event.latLng) {
                  const lat = event.latLng.lat();
                  const lng = event.latLng.lng();
                  const newPosition = { lat, lng };

                  setMarkerPosition(newPosition);
                  setMapCenter(newPosition);

                  performReverseGeocode(lat, lng);
                }
              }}
            />
          )}
        </GoogleMap>
      </div>) : null}
    </Space>
  );
};

export default MapWithAutocomplete;
